import {
  ActionsModel,
  AssetCategories,
  AssetListFileObject,
  CreativeAgencies,
  FieldType,
  FormControlModel,
  LufthansaContentOptions,
  LufthansaProductOptions,
  PROMPTS,
  StepperGroupModel,
  StepperModel,
  WidthClass,
} from '@thehubz/client/core';
import { UserInfo, UserValue } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import {
  AnyType,
  AssetMetadata,
  BuyoutRights,
  BuyoutRightsMakeOS,
  BuyoutRightsMakeOSSecondLevel,
  BuyoutRightsSeat,
  CreatorDepartmentStandard,
  CreatorDepartmentVorwerk,
  IMAGE_SOURCE,
  MediaCategories,
  MediaLists,
  TerritoryCategories,
  TerritoryCategoriesLufthansaGroup,
  TerritoryList,
  VERSIONS,
} from '@thehubz/shared/model';
import { StepperConfig } from '../../../models/stepper-config.interface';

export interface BuyoutFieldConfig {
  formFieldName: string;
  fieldLabel: string;
  fieldType: string;
  options: AnyType;
}

export type BuyoutRightsConfig = {
  [buyoutTypeId: string]: BuyoutFieldConfig[];
};

export const lufthansaTagFields: FormControlModel[] = [
  {
    formTypeId: '5',
    formFieldName: 'lufthansaContent',
    fieldType: FieldType.Dropdown,
    fieldLabel: $localize`Content`,
    placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
    widthClass: WidthClass.W12,
    value: undefined,
    mandatory: true,
    dropDownSearch: true,
    sortDataSource: false,
    options: LufthansaContentOptions,
    visible: false,
  },
  {
    formTypeId: '5',
    formFieldName: 'lufthansaProduct',
    fieldType: FieldType.Dropdown,
    fieldLabel: $localize`Product`,
    placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
    widthClass: WidthClass.W12,
    value: undefined,
    mandatory: true,
    dropDownSearch: true,
    sortDataSource: false,
    options: LufthansaProductOptions,
    visible: false,
  },
];

export const generalInformation = (): StepperGroupModel => ({
  stepperId: '1',
  stepperName: $localize`General information`,
  stepperGroupName: 'generalInformation',
  selected: true,
  visited: true,
  formFields: [
    {
      formTypeId: '1',
      formFieldName: 'name',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Asset name`,
      placeholder: $localize`:placeholder:Asset name`,
      inputType: 'text',
      validators: {
        required: true,
      },
      widthClass: WidthClass.WFull,
      value: '',
      visible: true,
    },
    {
      formTypeId: '2',
      formFieldName: 'status',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Status`,
      placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
      widthClass: WidthClass.W12,
      validators: {
        required: true,
      },
      value: undefined,
      options: [],
      visible: true,
    },
    {
      formTypeId: '5',
      formFieldName: 'agency',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Agency`,
      placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
      widthClass: WidthClass.W12,
      validators: {
        required: true,
      },
      value: undefined,
      dropDownSearch: true,
      options: [],
      visible: true,
    },
    {
      formTypeId: '6',
      formFieldName: 'client',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Client`,
      placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
      widthClass: WidthClass.W12,
      validators: {
        required: true,
      },
      value: undefined,
      dropDownSearch: true,
      options: [],
      visible: true,
    },
    {
      formTypeId: '7',
      formFieldName: 'brand',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Brand`,
      placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
      widthClass: WidthClass.W12,
      validators: {
        required: true,
      },
      value: undefined,
      dropDownSearch: true,
      options: [],
      visible: true,
    },
    {
      formTypeId: '8',
      formFieldName: 'assetCategory',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Asset category`,
      placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_MULTIPLE,
      widthClass: WidthClass.W12,
      multiple: true,
      sortDataSource: false,
      visible: true,
      validators: {
        required: true,
      },
      value: undefined,
      dropDownSearch: true,
      options: AssetCategories,
    },
    {
      formTypeId: '9',
      formFieldName: 'clientContact',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Licensee/Client contact`,
      placeholder: $localize`:placeholder:Licensee/Client contact`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
    },
    {
      formTypeId: '10',
      formFieldName: 'jobNumber',
      fieldType: FieldType.Number,
      fieldLabel: $localize`Job number`,
      placeholder: $localize`:placeholder:Job number`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
    },
    {
      formTypeId: '11',
      formFieldName: 'description',
      fieldType: FieldType.Textarea,
      fieldLabel: $localize`Description`,
      placeholder: $localize`Description`,
      inputType: 'text',
      widthClass: WidthClass.WFull,
      value: '',
      visible: true,
    },
    {
      formTypeId: '12',
      formFieldName: 'plannedDeletionToggle',
      fieldType: FieldType.Checkbox,
      fieldLabel: $localize`Planned for deletion`,
      placeholder: '',
      editOnly: true,
      widthClass: WidthClass.W13,
      value: '',
      visible: true,
    },
    {
      formTypeId: '13',
      formFieldName: 'plannedDeletion',
      fieldType: FieldType.Date,
      fieldLabel: $localize`Deletion date`,
      placeholder: $localize`DD/MM/YYYY`,
      minDate: new Date(),
      widthClass: WidthClass.W13,
      value: '',
      editOnly: true,
      disable: true,
      validators: {
        required: true,
      },
      visible: true,
    },
  ],
  actions: [
    {
      actionLabel: $localize`:action:Next`,
      actionType: 'next',
      goToStep: 2,
      actionTypePosition: 'right',
      icon: 'chevron_right',
      alignIcon: 'right',
      disable: true,
      visible: true,
    },
  ],
});
export const tags = (stepperConfig?: StepperConfig, nextActionObj?: ActionsModel): StepperGroupModel => ({
  stepperId: '2',
  stepperName: $localize`Tags & taxonomy`,
  stepperGroupName: AssetMetadata.General.TAGS,
  selected: false,
  visited: false,
  disable: true,
  role: 'tagging',
  formFields: [
    {
      formTypeId: '1',
      formFieldName: AssetMetadata.General.TAGS,
      fieldType: FieldType.MatChipList,
      fieldLabel: $localize`Tags`,
      placeholder: $localize`:placeholder:Enter tags`,
      widthClass: WidthClass.WFull,
      visible: true,
      disable: stepperConfig?.disableTag,
      options: [],
      value: [],
    },
  ],
  actions: [
    {
      actionLabel: $localize`:action:Back`,
      actionType: 'back',
      goToStep: 1,
      actionTypePosition: 'left',
      icon: 'chevron_left',
      alignIcon: 'left',
      disable: false,
      visible: true,
    },
    nextActionObj
      ? nextActionObj
      : {
          actionLabel: $localize`:action:Next`,
          actionType: 'next',
          goToStep: 3,
          actionTypePosition: 'right',
          icon: 'chevron_right',
          alignIcon: 'right',
          disable: false,
          visible: true,
        },
  ],
});
export const stepperConfigurations = (users: UserInfo[], stepperConfig?: StepperConfig): StepperModel => {
  return {
    stepID: 'assetUpload',
    stepLabel: $localize`New Asset`,
    stepper: [
      { ...generalInformation() },
      { ...tags(stepperConfig) },
      {
        stepperId: '3',
        stepperName: $localize`Relations: Select assets`,
        stepperGroupName: 'selectAssets',
        overrideHeader: $localize`Relations: Select assets to set a relation with uploaded assets`,
        selected: false,
        visited: false,
        disable: false,
        role: 'relations',
        formFields: [
          {
            formTypeId: '1',
            formFieldName: 'assetGallery',
            fieldType: FieldType.AssetGallery,
            fieldLabel: $localize`Select Assets`,
            placeholder: $localize`:placeholder:Select Assets`,
            widthClass: WidthClass.WFull,
            visible: true,
            value: [],
          },
        ],
        actions: [
          {
            actionLabel: $localize`:action:Back`,
            actionType: 'back',
            goToStep: 2,
            actionTypePosition: 'left',
            icon: 'chevron_left',
            alignIcon: 'left',
            visible: true,
          },
          {
            actionLabel: $localize`:action:Next`,
            goToStep: 4,
            actionType: 'selectAssets',
            actionTypePosition: 'right',
            icon: 'chevron_right',
            alignIcon: 'right',
            disable: false,
            visible: true,
          },
        ],
      },
      {
        stepperId: '4',
        stepperName: $localize`Relations: Define relation type`,
        stepperGroupName: 'defineAssetRelation',
        selected: false,
        visited: false,
        disable: true,
        role: 'relations',
        formFields: [
          {
            formTypeId: '1',
            formFieldName: 'assetRelation',
            fieldType: FieldType.AssetRelation,
            fieldLabel: $localize`Define asset relation`,
            placeholder: $localize`:placeholder:Define asset relation`,
            widthClass: WidthClass.WFull,
            visible: true,
            value: [],
          },
        ],
        actions: [
          {
            actionLabel: $localize`:action:Back`,
            actionType: 'back',
            goToStep: 3,
            actionTypePosition: 'left',
            icon: 'chevron_left',
            alignIcon: 'left',
            visible: true,
          },
          {
            actionLabel: $localize`:action:Next`,
            actionType: 'next',
            goToStep: 5,
            actionTypePosition: 'right',
            icon: 'chevron_right',
            alignIcon: 'right',
            disable: false,
            visible: true,
          },
        ],
      },
      {
        stepperId: '5',
        stepperName: $localize`Collection`,
        stepperGroupName: 'collection',
        selected: false,
        visited: false,
        disable: true,
        formFields: [
          {
            formTypeId: '2',
            formFieldName: 'selectType',
            fieldType: FieldType.Dropdown,
            fieldLabel: $localize`Select collection`,
            dropDownSearch: false,
            sortDataSource: false,
            placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
            inputType: '',
            widthClass: WidthClass.W12,
            disable: false,
            functionCall: true,
            visible: true,
            options: [
              {
                id: 'createNewCollection',
                name: $localize`Create new collection`,
              },
            ],
          },
        ],
        actions: [
          {
            actionLabel: $localize`:action:Back`,
            actionType: 'back',
            goToStep: 4,
            actionTypePosition: 'left',
            icon: 'chevron_left',
            alignIcon: 'left',
            visible: true,
          },
          {
            actionLabel: $localize`:action:Upload`,
            actionType: 'uploadAsset',
            actionTypePosition: 'right',
            icon: 'cloud_upload',
            alignIcon: 'left',
            disable: false,
            visible: true,
          },
          {
            actionLabel: $localize`:action:Next`,
            actionType: 'collection',
            goToStep: 7,
            actionTypePosition: 'right',
            icon: 'chevron_right',
            alignIcon: 'right',
            visible: false,
          },
        ],
      },
    ],
  };
};

export const buyoutRightsFields = (brandingKey: string): FormControlModel[] => {
  const creatorDepartment = creatorDepartmentObj(brandingKey);
  return [
    {
      formTypeId: '6',
      formFieldName: 'date-label',
      fieldLabel: '',
      fieldType: FieldType.Divider,
      visible: true,
      widthClass: WidthClass.WFull,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover', 'stockmaterial'],
    },
    {
      formTypeId: '2',
      formFieldName: 'orderNumber',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Order number`,
      placeholder: $localize`:placeholder:Order number`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover', 'stockmaterial'],
    },
    {
      formTypeId: '3',
      formFieldName: 'creatorDepartment',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Creator department`,
      placeholder: $localize`:placeholder:Creator department`,
      widthClass: WidthClass.W12,
      options: creatorDepartment,
      value: undefined,
      dropDownSearch: true,
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover'],
    },
    {
      formTypeId: '4',
      formFieldName: 'creatorContact',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Creator contact`,
      placeholder: $localize`:placeholder:Creator contact`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover'],
    },
    {
      formTypeId: '5',
      formFieldName: 'campaignStart',
      fieldType: FieldType.Date,
      fieldLabel: $localize`Campaign start`,
      placeholder: $localize`DD/MM/YYYY`,
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover'],
    },
    {
      formTypeId: '6',
      formFieldName: 'producerAgency',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Producer agency`,
      placeholder: $localize`:placeholder:Producer agency`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover'],
    },
    {
      formTypeId: '7',
      formFieldName: 'producerAgencyContact',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Producer agency contact`,
      placeholder: $localize`:placeholder:Producer agency contact`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover'],
    },
    {
      formTypeId: '8',
      formFieldName: 'producingDateYear',
      fieldType: FieldType.Date,
      fieldLabel: $localize`Producing date/year`,
      placeholder: $localize`DD/MM/YYYY`,
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['video', 'image', 'compositionmusic', 'voiceover'],
    },
    {
      formTypeId: '9',
      formFieldName: 'supplierLibrary',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Supplier/Library`,
      placeholder: $localize`:placeholder:Supplier/Library`,
      inputType: 'text',
      widthClass: WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['stockmaterial'],
    },
  ];
};

export const buyoutRightsFieldsSeat = (newStepperId?: string): FormControlModel[] => {
  return [
    {
      formTypeId: '2',
      formFieldName: 'title',
      fieldType: FieldType.Text,
      fieldLabel: 'Title',
      placeholder: 'Title',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['music'],
    },
    {
      formTypeId: '2',
      formFieldName: 'composer',
      fieldType: FieldType.Text,
      fieldLabel: 'Composer',
      placeholder: 'Composer',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['music'],
    },
    {
      formTypeId: '2',
      formFieldName: 'performer',
      fieldType: FieldType.Text,
      fieldLabel: 'Performer',
      placeholder: 'Performer',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['music'],
    },
    {
      formTypeId: '2',
      formFieldName: 'label',
      fieldType: FieldType.Text,
      fieldLabel: 'Label',
      placeholder: 'Label',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['music'],
    },
    {
      formTypeId: '2',
      formFieldName: 'speakerAgency',
      fieldType: FieldType.Text,
      fieldLabel: 'Sprecher/Agentur',
      placeholder: 'Sprecher/Agentur',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['voiceover'],
    },
    {
      formTypeId: '6',
      formFieldName: '',
      fieldLabel: 'Nutzungsrechte',
      fieldType: FieldType.Label,
      visible: true,
      widthClass: WidthClass.WFull,
      linkableArray: ['image', 'talents', 'music', 'voiceover'],
    },
    {
      formTypeId: '2',
      formFieldName: 'contactPerson',
      fieldType: FieldType.Text,
      fieldLabel: 'Ansprechpartner bei Buyout-Fragen',
      placeholder: 'Ansprechpartner bei Buyout-Fragen',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['image', 'talents', 'music', 'voiceover'],
    },
    {
      formTypeId: '2',
      formFieldName: 'talentName',
      fieldType: FieldType.Text,
      fieldLabel: 'Name(n) Talent(s) / Agentur (wenn bekannt)',
      placeholder: 'Name(n) Talent(s) / Agentur (wenn bekannt)',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['talents'],
    },
    {
      formTypeId: '2',
      formFieldName: 'distribution',
      fieldType: FieldType.Text,
      fieldLabel: 'Verbreitung/Kanäle',
      placeholder: 'Verbreitung/Kanäle',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['image', 'talents', 'music', 'voiceover'],
    },
    {
      formTypeId: '2',
      formFieldName: 'durationPeriod',
      fieldType: FieldType.Text,
      fieldLabel: 'Dauer/Zeitraum',
      placeholder: 'Dauer/Zeitraum',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['image', 'talents', 'music', 'voiceover'],
    },
    {
      formTypeId: '5',
      formFieldName: 'expiryDate',
      fieldType: FieldType.Date,
      fieldLabel: 'Gültig bis',
      repeatFieldLabel: true,
      placeholder: $localize`DD/MM/YYYY`,
      mandatory: true,
      validators: {
        required: true,
      },
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['image', 'talents', 'music', 'voiceover'],
    },
    {
      formTypeId: '5',
      formFieldName: 'firstAiring',
      fieldType: FieldType.Date,
      repeatFieldLabel: true,
      fieldLabel: 'First Airing',
      placeholder: $localize`DD/MM/YYYY`,
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['talents'],
    },
    {
      formTypeId: '6',
      formFieldName: '',
      fieldLabel: 'Bildinformationen',
      fieldType: FieldType.Label,
      visible: true,
      widthClass: WidthClass.WFull,
      linkableArray: ['image'],
    },
    {
      formTypeId: '3',
      formFieldName: 'version',
      fieldType: FieldType.MultiSelectChip,
      fieldLabel: 'Version',
      placeholder: 'Version',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      options: VERSIONS,
      value: undefined,
      dropDownSearch: true,
      visible: true,
      linkableArray: ['image'],
    },
    {
      formTypeId: '3',
      formFieldName: 'imageSource',
      fieldType: FieldType.Dropdown,
      fieldLabel: 'Bildquelle',
      placeholder: 'Bildquelle',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      options: IMAGE_SOURCE,
      repeatFieldLabel: true,
      value: undefined,
      dropDownSearch: true,
      visible: true,
      linkableArray: ['image'],
    },
    {
      formTypeId: '2',
      formFieldName: 'additionalInformation',
      fieldType: FieldType.Text,
      fieldLabel: 'Sonstige',
      placeholder: 'Sonstige',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: false,
      linkableArray: ['image'],
    },
    {
      formTypeId: '2',
      formFieldName: 'imageTitle',
      fieldType: FieldType.Text,
      fieldLabel: 'Bildtitel',
      placeholder: 'Bildtitel',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['image'],
    },
    {
      formTypeId: '2',
      formFieldName: 'referenceNumber',
      fieldType: FieldType.Text,
      fieldLabel: 'Reference No./ID',
      placeholder: 'Reference No./ID',
      inputType: 'text',
      widthClass: newStepperId ? WidthClass.W14 : WidthClass.W12,
      value: '',
      visible: true,
      linkableArray: ['image'],
    },
    {
      formTypeId: '4',
      formFieldName: 'importantInstruction',
      fieldType: FieldType.Textarea,
      fieldLabel: 'Wichtige Hinweise',
      placeholder: 'Wichtige Hinweise',
      inputType: 'text',
      mandatory: true,
      validators: {
        required: true,
      },
      widthClass: WidthClass.WFull,
      value: '',
      visible: true,
      linkableArray: ['Zusatzinformationen'],
    },
  ];
};

export const buyoutRightsFormArrayFields: FormControlModel = {
  formTypeId: '2',
  formFieldName: 'buyoutRightsFormArray',
  fieldType: FieldType.FormArray,
  fieldLabel: '',
  hideLabel: true,
  placeholder: '',
  inputType: '',
  mandatory: false,
  widthClass: WidthClass.WFull,
  disable: false,
  visible: true,
};

export const musicFormFields: FormControlModel[] = [
  {
    formTypeId: '3',
    formFieldName: 'composerName',
    fieldType: FieldType.Text,
    fieldLabel: $localize`Composer name & GEMA No.`,
    placeholder: $localize`:placeholder:Composer name & GEMA No.`,
    inputType: 'text',
    widthClass: WidthClass.W12,
    value: '',
    visible: false,
    validators: {
      required: true,
    },
    linkableArray: ['archive', 'composed', 'licensed'],
  },
  {
    formTypeId: '4',
    formFieldName: 'publisherName',
    fieldType: FieldType.Text,
    fieldLabel: $localize`Publisher name & GEMA No.`,
    placeholder: $localize`:placeholder:Publisher name & GEMA No.`,
    inputType: 'text',
    widthClass: WidthClass.W12,
    value: '',
    visible: false,
    validators: {
      required: true,
    },
    linkableArray: ['archive', 'composed', 'licensed'],
  },
];

export const buyoutRightsFieldsMakeOS = (isLufthansaGroupClient?: boolean): FormControlModel[] => {
  return [
    {
      ...buyoutRightsFormArrayFields,
      linkableArray: ['cast', 'voiceover'],
      formFieldGroup: [
        {
          formTypeId: '1',
          formFieldName: 'name',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Name`,
          placeholder: $localize`:placeholder:Name`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: true,
          validators: {
            required: true,
          },
          linkableArray: ['cast', 'voiceover'],
        },
        {
          formTypeId: '2',
          formFieldName: 'role',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Role`,
          placeholder: $localize`:placeholder:Role`,
          widthClass: WidthClass.W12,
          value: '',
          visible: true,
          linkableArray: ['cast', 'voiceover'],
        },
        {
          formTypeId: '3',
          formFieldName: 'agencyNContactInfo',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Agency & Contact info`,
          placeholder: $localize`:placeholder:Agency & Contact info`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: true,
          linkableArray: ['cast', 'voiceover'],
        },
        {
          formTypeId: '4',
          formFieldName: 'territoriesFormArray',
          fieldType: FieldType.FormArray,
          fieldLabel: '',
          hideLabel: true,
          placeholder: '',
          inputType: '',
          mandatory: false,
          widthClass: WidthClass.WFull,
          disable: false,
          visible: true,
          formFieldGroup: territoriesFormArray(isLufthansaGroupClient),
          formArrayActionType: 'add-n-remove',
          linkableArray: ['cast', 'voiceover'],
        },
      ],
      formArrayActionType: 'single-button',
      noTopBorder: true,
    },
    {
      ...buyoutRightsFormArrayFields,
      linkableArray: ['director', 'dop', 'photographer', 'contentcreator', 'illustrator', 'other'],
      formFieldGroup: [
        {
          formTypeId: '1',
          formFieldName: 'name',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Name`,
          placeholder: $localize`:placeholder:Name`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: true,
          validators: {
            required: true,
          },
          linkableArray: ['director', 'dop', 'photographer', 'contentcreator', 'illustrator', 'other'],
        },
        {
          formTypeId: '2',
          formFieldName: 'agencyNContactInfo',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Agency & Contact info`,
          placeholder: $localize`:placeholder:Agency & Contact info`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: true,
          linkableArray: ['director', 'dop', 'photographer', 'contentcreator', 'illustrator', 'other'],
        },
        {
          formTypeId: '4',
          formFieldName: 'territoriesFormArray',
          fieldType: FieldType.FormArray,
          fieldLabel: '',
          hideLabel: true,
          placeholder: '',
          inputType: '',
          mandatory: false,
          widthClass: WidthClass.WFull,
          disable: false,
          visible: true,
          formFieldGroup: territoriesFormArray(isLufthansaGroupClient),
          formArrayActionType: 'add-n-remove',
          linkableArray: ['director', 'dop', 'photographer', 'contentcreator', 'illustrator', 'other'],
        },
      ],
      noTopBorder: true,
    },
    {
      ...buyoutRightsFormArrayFields,
      linkableArray: ['archive', 'composed', 'licensed'],
      formFieldGroup: [
        {
          formTypeId: '1',
          formFieldName: 'name',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Archive name`,
          placeholder: $localize`:placeholder:Archive name`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: false,
          validators: {
            required: true,
          },
          linkableArray: ['archive'],
        },
        {
          formTypeId: '1',
          formFieldName: 'musicCompany',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Music company`,
          placeholder: $localize`:placeholder:Music company`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: false,
          validators: {
            required: true,
          },
          linkableArray: ['composed'],
        },
        {
          formTypeId: '2',
          formFieldName: 'title',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Title`,
          placeholder: $localize`:placeholder:Title`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: false,
          validators: {
            required: true,
          },
          linkableArray: ['archive', 'composed', 'licensed'],
        },
        {
          formTypeId: '2',
          formFieldName: 'contactInfo',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Contact info`,
          placeholder: $localize`:placeholder:Contact info`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: false,
          linkableArray: ['composed'],
        },
        {
          formTypeId: '2',
          formFieldName: 'musicianBand',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Musician/band`,
          placeholder: $localize`:placeholder:Musician/band`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: false,
          validators: {
            required: true,
          },
          linkableArray: ['licensed'],
        },
        {
          formTypeId: '3',
          formFieldName: 'masterRight',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Master right (name & contact info)`,
          placeholder: $localize`:placeholder:Master right (name & contact info)`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: false,
          validators: {
            required: true,
          },
          linkableArray: ['licensed'],
        },
        {
          formTypeId: '4',
          formFieldName: 'publisherRights',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Publisher rights (name & contact info)`,
          placeholder: $localize`:placeholder:Publisher rights (name & contact info)`,
          inputType: 'text',
          widthClass: WidthClass.W12,
          value: '',
          visible: false,
          validators: {
            required: true,
          },
          linkableArray: ['licensed'],
        },
        ...musicFormFields,
        {
          formTypeId: '4',
          formFieldName: 'territoriesFormArray',
          fieldType: FieldType.FormArray,
          fieldLabel: '',
          hideLabel: true,
          placeholder: '',
          inputType: '',
          mandatory: false,
          widthClass: WidthClass.WFull,
          disable: false,
          visible: true,
          formFieldGroup: territoriesFormArray(isLufthansaGroupClient),
          formArrayActionType: 'add-n-remove',
          linkableArray: ['archive'],
        },
      ],
      noTopBorder: true,
    },
    {
      ...buyoutRightsFormArrayFields,
      linkableArray: ['movingimage', 'stillimage', 'stockmusic', 'sound', 'graphic'],
      formFieldGroup: [
        {
          formTypeId: '2',
          formFieldName: 'stockRightHolderNameNContactInfo',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Stock right holder (name & contact info)`,
          placeholder: $localize`:placeholder:Stock right holder (name & contact info)`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: true,
          validators: {
            required: true,
          },
          linkableArray: ['movingimage', 'stillimage', 'stockmusic', 'sound', 'graphic'],
        },
        {
          formTypeId: '7',
          formFieldName: 'groupCorporateLicense',
          fieldType: FieldType.Checkbox,
          fieldLabel: $localize`Group/corporate license`,
          placeholder: '',
          widthClass: 'w-[24%]',
          value: false,
          validators: {
            required: false,
          },
          visible: true,
          linkableArray: ['movingimage', 'stillimage'],
        },
        {
          formTypeId: '4',
          formFieldName: 'territoriesFormArray',
          fieldType: FieldType.FormArray,
          fieldLabel: '',
          hideLabel: true,
          placeholder: '',
          inputType: '',
          mandatory: false,
          widthClass: WidthClass.WFull,
          disable: false,
          visible: true,
          formFieldGroup: territoriesFormArray(isLufthansaGroupClient),
          formArrayActionType: 'add-n-remove',
          linkableArray: ['movingimage', 'stillimage', 'stockmusic', 'sound', 'graphic'],
        },
      ],
      noTopBorder: true,
    },
    {
      ...buyoutRightsFormArrayFields,
      linkableArray: ['others'],
      formFieldGroup: [
        {
          formTypeId: '1',
          formFieldName: 'type',
          fieldType: FieldType.Text,
          fieldLabel: $localize`Type`,
          placeholder: $localize`:placeholder:Type`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: true,
          validators: {
            required: true,
          },
          linkableArray: ['others'],
        },
        {
          formTypeId: '2',
          formFieldName: 'rightsHolder',
          fieldType: FieldType.Textarea,
          fieldLabel: $localize`Rights holder (name & contact info)`,
          placeholder: $localize`:placeholder:Rights holder (name & contact info)`,
          inputType: 'text',
          widthClass: WidthClass.WFull,
          value: '',
          visible: true,
          validators: {
            required: true,
          },
          linkableArray: ['others'],
        },
        {
          formTypeId: '4',
          formFieldName: 'territoriesFormArray',
          fieldType: FieldType.FormArray,
          fieldLabel: '',
          hideLabel: true,
          placeholder: '',
          inputType: '',
          mandatory: false,
          widthClass: WidthClass.WFull,
          disable: false,
          visible: true,
          formFieldGroup: territoriesFormArray(isLufthansaGroupClient),
          formArrayActionType: 'add-n-remove',
          linkableArray: ['others'],
        },
      ],
      formArrayActionType: 'single-button',
      noTopBorder: true,
    },
  ];
};

export const territoriesFormArray = (isLufthansaGroupClient?: boolean): FormControlModel[] => {
  let territoryCategories = [...TerritoryCategories];
  if (isLufthansaGroupClient) {
    territoryCategories = [...TerritoryCategories, ...TerritoryCategoriesLufthansaGroup];
  }

  return [
    {
      formTypeId: '1',
      formFieldName: 'territory',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Territory`,
      placeholder: $localize`:placeholder:Territory`,
      widthClass: 'w-[32%]',
      options: [...territoryCategories, ...TerritoryList],
      value: '',
      dropDownSearch: true,
      infotext: $localize`Please check if the listing/category is suitable for the asset`,
      visible: true,
      matDivider: territoryCategories.length - 1,
      sortDataSource: false,
      multiple: true,
      validators: {
        required: true,
      },
    },
    {
      formTypeId: '2',
      formFieldName: 'media',
      fieldType: FieldType.Dropdown,
      fieldLabel: $localize`Media`,
      placeholder: $localize`:placeholder:Media`,
      widthClass: 'w-[32%]',
      options: [...MediaCategories, ...MediaLists],
      value: '',
      dropDownSearch: true,
      infotext: $localize`Please check if the listing/category is suitable for the asset`,
      visible: true,
      matDivider: MediaCategories.length - 1,
      sortDataSource: false,
      multiple: true,
      validators: {
        required: true,
      },
    },
    {
      formTypeId: '3',
      formFieldName: 'additionalMediaInfo',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Additional media info`,
      placeholder: $localize`:placeholder:Additional media info`,
      inputType: 'text',
      infotext: $localize`Please add additional info e.g. paid/unpaid, limitations, etc.`,
      widthClass: 'w-[32%]',
      value: '',
      visible: true,
    },
    {
      formTypeId: '4',
      formFieldName: 'startBuyout',
      fieldType: FieldType.Date,
      fieldLabel: $localize`Start buyout`,
      placeholder: $localize`DD/MM/YYYY`,
      widthClass: 'w-[15.5%]',
      value: '',
      visible: true,
      validators: {
        required: true,
      },
    },
    {
      formTypeId: '5',
      formFieldName: 'duration',
      fieldType: FieldType.Text,
      fieldLabel: $localize`Duration`,
      placeholder: $localize`:placeholder:Duration`,
      inputType: 'text',
      widthClass: 'w-[15.5%]',
      value: '',
      visible: true,
    },
    {
      formTypeId: '6',
      formFieldName: 'expiryDate',
      fieldType: FieldType.Date,
      fieldLabel: $localize`Expiry date`,
      placeholder: $localize`DD/MM/YYYY`,
      widthClass: 'w-[32%]',
      validators: {
        required: true,
      },
      value: '',
      visible: true,
    },
    {
      formTypeId: '7',
      formFieldName: 'noExpiry',
      fieldType: FieldType.Checkbox,
      fieldLabel: $localize`No Expiry date`,
      placeholder: '',
      widthClass: 'w-[24%]',
      value: false,
      validators: {
        required: false,
      },
      visible: true,
    },
  ];
};

export const buyoutRightsSecondLevelField = (
  secondLevelLabel: string,
  secondLevelOptions: { _id: string; name: string; firstLvl: string }[],
): FormControlModel[] => {
  const fieldLabel = ['Music', 'Stock'].includes(secondLevelLabel)
    ? $localize`Select ${secondLevelLabel.toLowerCase()} type`
    : $localize`Add a ${secondLevelLabel}`;
  return [
    {
      formTypeId: '4',
      formFieldName: 'buyoutRightsSecondLevelType',
      fieldType: FieldType.Dropdown,
      fieldLabel: fieldLabel,
      placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
      widthClass: WidthClass.WFull,
      extraClasses: 'mt-3',
      sortDataSource: false,
      value: '',
      validators: {
        required: true,
      },
      disable: false,
      options: secondLevelOptions,
      subscriptSizing: 'dynamic',
      visible: true,
    },
  ];
};

export const buyoutRightsField = (availableTitles: string[], disableState?: boolean): FormControlModel => {
  return {
    formTypeId: '2',
    formFieldName: 'buyoutRightsTitle',
    fieldType: FieldType.Dropdown,
    fieldLabel: $localize`Buyout rights`,
    placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
    widthClass: WidthClass.WFull,
    sortDataSource: false,
    value: '',
    validators: {
      required: true,
    },
    disable: disableState ?? false,
    options: BuyoutRights.filter((right) => !availableTitles.includes(right._id)),
    subscriptSizing: 'dynamic',
    visible: true,
  };
};

export const buyoutRightsFieldMakeOS = (
  availableTitles: string[],
  disableState?: boolean,
  buyoutRightsTitleValue?: AnyType,
  isRequired = true,
  newStepperId?: string,
): FormControlModel => {
  const buyoutRightDropdown: FormControlModel = {
    formTypeId: '2',
    formFieldName: 'buyoutRightsTitle',
    fieldType: FieldType.Dropdown,
    fieldLabel: $localize`Buyout rights`,
    placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
    widthClass: WidthClass.WFull,
    sortDataSource: false,
    value: buyoutRightsTitleValue || '',
    validators: {
      required: isRequired,
    },
    disable: disableState ?? false,
    options: BuyoutRightsMakeOS.filter((right) => !availableTitles.includes(right._id)),
    subscriptSizing: 'dynamic',
    visible: true,
  };
  if (newStepperId) {
    const buyoutRightFormArray: FormControlModel = {
      formTypeId: '3',
      formFieldName: 'usageRights',
      fieldType: FieldType.FormArray,
      fieldLabel: $localize`Usages rights`,
      hideLabel: true,
      widthClass: WidthClass.WFull,
      visible: true,
      formFieldGroup: [buyoutRightDropdown],
    };
    return buyoutRightFormArray;
  }
  return buyoutRightDropdown;
};

export const buyoutRightsFieldSeat = (availableTitles: string[], disableState?: boolean, newStepperId?: string): FormControlModel => {
  const buyoutRightDropdown: FormControlModel = {
    formTypeId: '2',
    formFieldName: 'buyoutRightsTitle',
    fieldType: FieldType.Dropdown,
    fieldLabel: $localize`Buyout rights`,
    placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
    widthClass: WidthClass.WFull + ' pb-4',
    sortDataSource: false,
    repeatFieldLabel: true,
    value: '',
    validators: {
      required: true,
    },
    disable: disableState ?? false,
    options: BuyoutRightsSeat.filter((right) => !availableTitles.includes(right._id)),
    subscriptSizing: 'dynamic',
    visible: true,
  };

  if (newStepperId) {
    const allSeatBuyoutRightsFields = buyoutRightsFieldsSeat(newStepperId);
    const buyoutRightFormArray: FormControlModel = {
      formTypeId: '3',
      formFieldName: 'usageRights',
      fieldType: FieldType.FormArray,
      fieldLabel: $localize`Usages rights`,
      hideLabel: true,
      widthClass: WidthClass.WFull,
      visible: true,
      formFieldGroup: [buyoutRightDropdown, ...allSeatBuyoutRightsFields],
    };
    return buyoutRightFormArray;
  } else {
    return buyoutRightDropdown;
  }
};

export const getBuyoutRightsConfig = (brandingKey: string) => {
  const configObject: BuyoutRightsConfig = {};
  let buyoutTypes = [];
  let buyoutRightsFieldsData: FormControlModel[] = [];

  switch (brandingKey) {
    case CustomClients.SEAT:
      buyoutTypes = BuyoutRightsSeat;
      buyoutRightsFieldsData = buyoutRightsFieldsSeat();
      break;
    case CustomClients.Standard:
      buyoutTypes = [...BuyoutRightsMakeOS, ...BuyoutRightsMakeOSSecondLevel];
      buyoutRightsFieldsData = buyoutRightsFieldsMakeOS()
        .flatMap((field) => field.formFieldGroup)
        .filter((field): field is FormControlModel => field !== undefined);
      break;
    default:
      buyoutTypes = BuyoutRights;
      buyoutRightsFieldsData = buyoutRightsFields(brandingKey);
      break;
  }

  buyoutTypes.forEach((buyoutType: AnyType) => {
    configObject[buyoutType._id] = buyoutRightsFieldsData
      .filter((fields) => !['divider', 'formArray'].includes(fields.fieldType) && fields.linkableArray?.includes(buyoutType._id))
      .map((fields) => ({
        formFieldName: fields.formFieldName,
        fieldLabel: fields.fieldLabel,
        fieldType: fields.fieldType,
        options: fields.options,
      }));
  });

  return configObject;
};

export const buyoutRightConfigs = (brandingKey: string): Partial<FormControlModel>[] => {
  return buyoutRightsFields(brandingKey)
    .filter((fields) => fields.fieldType !== 'divider')
    .map((fields) => ({
      formFieldName: fields.formFieldName,
      fieldLabel: fields.fieldLabel,
      options: fields.options,
    }));
};

export const buyoutRightConfigsSEAT = (): Partial<FormControlModel>[] => {
  return buyoutRightsFieldsSeat()
    .filter((fields) => fields.fieldType !== 'divider')
    .map((fields) => ({
      formFieldName: fields.formFieldName,
      fieldLabel: fields.fieldLabel,
      fieldType: fields.fieldType,
      options: fields.options,
      linkableArray: fields.linkableArray,
    }));
};

export const usageRightConfigs = (users: UserInfo[]): StepperGroupModel => {
  return {
    stepperId: '3',
    stepperName: $localize`Usage rights`,
    stepperGroupName: 'usageRight',
    selected: false,
    visited: false,
    disable: true,
    role: 'usagerights',
    formFields: [
      {
        formTypeId: '1',
        formFieldName: 'owner',
        fieldType: FieldType.Dropdown,
        fieldLabel: $localize`Owner`,
        placeholder: $localize`:placeholder:Owner`,
        widthClass: WidthClass.WFull,
        options: users,
        value: undefined,
        dropDownSearch: true,
        validators: {
          required: true,
        },
        visible: true,
      },
      {
        formTypeId: '2',
        formFieldName: 'expiryDate',
        fieldType: FieldType.Date,
        fieldLabel: $localize`Expiry date`,
        placeholder: $localize`DD/MM/YYYY`,
        minDate: new Date(),
        widthClass: WidthClass.W12,
        value: '',
        validators: {
          required: true,
        },
        visible: true,
      },
      {
        formTypeId: '3',
        formFieldName: 'noExpiry',
        fieldType: FieldType.Checkbox,
        fieldLabel: $localize`No Expiry date`,
        placeholder: '',
        widthClass: WidthClass.W12,
        value: '',
        validators: {
          required: false,
        },
        visible: true,
      },
      {
        formTypeId: '4',
        formFieldName: 'notes',
        fieldType: FieldType.Textarea,
        fieldLabel: $localize`Source and notes`,
        placeholder: $localize`:placeholder:Source and notes`,
        inputType: 'text',
        widthClass: WidthClass.WFull,
        value: '',
        visible: true,
      },
    ],
    actions: [
      {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: 0, // this will be re-sequenced when steps are reconfigured in the method (updateStepperActionGoToStep)
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        disable: false,
        visible: true,
      },
      {
        actionLabel: $localize`:action:Next`,
        actionType: 'next',
        goToStep: 0, // this will be re-sequenced when steps are reconfigured in the method (updateStepperActionGoToStep)
        actionTypePosition: 'right',
        icon: 'chevron_right',
        alignIcon: 'right',
        disable: false,
        visible: true,
      },
    ],
  };
};

export const productionDetailsStepperConfigs = (): StepperGroupModel => {
  return {
    stepperId: '3',
    stepperName: $localize`Production details`,
    stepperGroupName: 'productionDetails',
    selected: false,
    visited: false,
    noValidationFlag: true,
    disable: true,
    formFields: [
      {
        formTypeId: '13',
        formFieldName: 'date-label',
        fieldLabel: $localize`General`,
        fieldType: FieldType.Label,
        placeholder: '',
        visible: true,
        widthClass: WidthClass.WFull,
      },
      {
        formTypeId: '9',
        formFieldName: 'creativeAgency',
        fieldType: FieldType.Dropdown,
        fieldLabel: $localize`Creative agency`,
        placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
        inputType: '',
        mandatory: false,
        dropDownSearch: true,
        widthClass: WidthClass.W14,
        disable: false,
        visible: true,
        options: CreativeAgencies,
      },
      {
        formTypeId: '1',
        formFieldName: 'creation',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Creation`,
        placeholder: $localize`Creation`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'account',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Account`,
        placeholder: $localize`Account`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'generalOthers',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Others`,
        placeholder: $localize`Others`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '3',
        formFieldName: 'shootingDetails',
        fieldType: FieldType.FormArray,
        fieldLabel: $localize`Shooting details`,
        hideLabel: true,
        widthClass: WidthClass.W12,
        visible: true,
        formFieldGroup: [
          {
            formTypeId: '1',
            formFieldName: 'shootingLocation',
            fieldType: FieldType.Text,
            fieldLabel: $localize`Shooting location`,
            placeholder: $localize`Shooting location`,
            inputType: 'text',
            repeatFieldLabel: true,
            widthClass: WidthClass.W14,
            visible: true,
            value: '',
          },
          {
            formTypeId: '3',
            formFieldName: 'shootingDate',
            fieldType: FieldType.Date,
            fieldLabel: $localize`Shooting date`,
            placeholder: $localize`DD/MM/YYYY`,
            repeatFieldLabel: true,
            widthClass: WidthClass.W14,
            value: '',
            visible: true,
          },
        ],
      },
      {
        formTypeId: '10',
        formFieldName: '',
        fieldType: FieldType.Space,
        fieldLabel: '',
        inputType: '',
        widthClass: WidthClass.W12,
        hideLabel: false,
        visible: true,
      },
      {
        formTypeId: '13',
        formFieldName: 'date-label',
        fieldLabel: $localize`Production`,
        fieldType: FieldType.Label,
        placeholder: '',
        visible: true,
        widthClass: WidthClass.WFull,
      },
      {
        formTypeId: '1',
        formFieldName: 'productionCompany',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Production company`,
        placeholder: $localize`Production company`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'executiveProducer',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Executive producer`,
        placeholder: $localize`Executive producer`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'producer',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Producer`,
        placeholder: $localize`Producer`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'director',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Director`,
        placeholder: $localize`Director`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'doP',
        fieldType: FieldType.Text,
        fieldLabel: $localize`DoP`,
        placeholder: $localize`DoP`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'specialCrewMembers',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Special crew members`,
        placeholder: $localize`Special crew members`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'photographer',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Photographer`,
        placeholder: $localize`Photographer`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'contentCreator',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Content creator`,
        placeholder: $localize`Content creator`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'illustrator',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Illustrator`,
        placeholder: $localize`Illustrator`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'productionOthers',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Others`,
        placeholder: $localize`Others`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '13',
        formFieldName: 'date-label',
        fieldLabel: $localize`Postproduction / Sound/Music`,
        fieldType: FieldType.Label,
        placeholder: '',
        visible: true,
        widthClass: WidthClass.WFull,
      },
      {
        formTypeId: '3',
        formFieldName: 'negativeDetails',
        fieldType: FieldType.FormArray,
        fieldLabel: $localize`Negative details`,
        hideLabel: true,
        widthClass: WidthClass.W12,
        visible: true,
        formFieldGroup: [
          {
            formTypeId: '1',
            formFieldName: 'negativeFormat',
            fieldType: FieldType.Text,
            fieldLabel: $localize`Negative format`,
            placeholder: $localize`Negative format`,
            inlineImage: true,
            infotext: $localize`What is the raw material, how was it shoot (i.e. film (analog 35mm), film (digital 4K), etc.)`,
            inputType: 'text',
            repeatFieldLabel: false,
            widthClass: WidthClass.W14,
            visible: true,
            value: '',
          },
          {
            formTypeId: '1',
            formFieldName: 'negativeStorage',
            fieldType: FieldType.Text,
            fieldLabel: $localize`Negative storage`,
            placeholder: $localize`Negative storage`,
            inputType: 'text',
            repeatFieldLabel: false,
            widthClass: WidthClass.W14,
            visible: true,
            value: '',
          },
        ],
      },
      {
        formTypeId: '10',
        formFieldName: '',
        fieldType: FieldType.Space,
        fieldLabel: '',
        inputType: '',
        widthClass: WidthClass.W12,
        hideLabel: false,
        visible: true,
      },
      {
        formTypeId: '1',
        formFieldName: 'masterFormat',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Master format`,
        placeholder: $localize`Master format`,
        inputType: 'text',
        inlineImage: true,
        infotext: $localize`In which format are the assets available in maximum quality? (i.e. HD, 4K, 19990x29299)`,
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'masterStorage',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Master storage`,
        placeholder: $localize`Master storage`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'offlineEditCompanyEditor',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Offline edit company (Editor)`,
        placeholder: $localize`Offline edit company (Editor)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'gradingCompanyArtist',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Grading company (Artist)`,
        placeholder: $localize`Grading company (Artist)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'onlineCompany',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Online (Company)`,
        placeholder: $localize`Online (Company)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'onlineProducer',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Online (Producer)`,
        placeholder: $localize`Online (Producer)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'musicCompanyArchive',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Music company, Archive`,
        placeholder: $localize`Music company, Archive`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'musicProducer',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Music (Producer)`,
        placeholder: $localize`Music (Producer)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '3',
        formFieldName: 'soundStudioDetails',
        fieldType: FieldType.FormArray,
        fieldLabel: $localize`Sound studio details`,
        hideLabel: true,
        widthClass: WidthClass.W34,
        visible: true,
        formFieldGroup: [
          {
            formTypeId: '1',
            formFieldName: 'soundStudioCompany',
            fieldType: FieldType.Text,
            fieldLabel: $localize`Sound studio (Company)`,
            placeholder: $localize`Sound studio (Company)`,
            inputType: 'text',
            repeatFieldLabel: false,
            widthClass: WidthClass.W14,
            visible: true,
            value: '',
          },
          {
            formTypeId: '1',
            formFieldName: 'soundStudioProducer',
            fieldType: FieldType.Text,
            fieldLabel: $localize`Sound studio (Producer)`,
            placeholder: $localize`Sound studio (Producer)`,
            inputType: 'text',
            repeatFieldLabel: false,
            widthClass: WidthClass.W14,
            visible: true,
            value: '',
          },
          {
            formTypeId: '3',
            formFieldName: 'soundRecordingSession',
            fieldType: FieldType.Date,
            fieldLabel: $localize`Sound recording session`,
            placeholder: $localize`DD/MM/YYYY`,
            repeatFieldLabel: true,
            widthClass: WidthClass.W14,
            value: '',
            visible: true,
          },
        ],
      },
      {
        formTypeId: '10',
        formFieldName: '',
        fieldType: FieldType.Space,
        fieldLabel: '',
        inputType: '',
        widthClass: WidthClass.W14,
        hideLabel: false,
        visible: true,
      },
      {
        formTypeId: '1',
        formFieldName: 'soundMasterStorage',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Sound master storage`,
        placeholder: $localize`Sound master storage`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'photoPostCompany',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Photo post (Company)`,
        placeholder: $localize`Photo post (Company)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'photoPostProducer',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Photo post (Producer)`,
        placeholder: $localize`Photo post (Producer)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'photoAnalogProcessingCompany',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Photo analog processing (Company)`,
        placeholder: $localize`Photo analog processing (Company)`,
        inputType: 'text',
        repeatFieldLabel: false,
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
      {
        formTypeId: '1',
        formFieldName: 'postProductionOthers',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Others`,
        placeholder: $localize`Others`,
        inputType: 'text',
        widthClass: WidthClass.W14,
        visible: true,
        value: '',
      },
    ],
    actions: [
      {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: 0, // this will be re-sequenced when steps are reconfigured in the method (updateStepperActionGoToStep)
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        disable: false,
        visible: true,
      },
      {
        actionLabel: $localize`:action:Next`,
        actionType: 'next',
        goToStep: 0, // this will be re-sequenced when steps are reconfigured in the method (updateStepperActionGoToStep)
        actionTypePosition: 'right',
        icon: 'chevron_right',
        alignIcon: 'right',
        disable: false,
        visible: true,
      },
    ],
  };
};

export const contentDescriptionConfigs = (): StepperGroupModel => {
  return {
    stepperId: '2',
    stepperName: 'Inhaltsbeschreibung',
    stepperGroupName: 'contentDescription',
    selected: false,
    visited: false,
    disable: true,
    formFields: [],
    actions: [
      {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: 0, // this will be re-sequenced when steps are reconfigured in the method (updateStepperActionGoToStep)
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        disable: false,
        visible: true,
      },
      {
        actionLabel: $localize`:action:Next`,
        actionType: 'next',
        goToStep: 0, // this will be re-sequenced when steps are reconfigured in the method (updateStepperActionGoToStep)
        actionTypePosition: 'right',
        icon: 'chevron_right',
        alignIcon: 'right',
        disable: false,
        visible: true,
      },
    ],
  };
};

export const createNewCollectionStepperConfig = (
  assetListForCollection: AssetListFileObject[],
  usersLikeMe: UserValue[],
  brandingKey: CustomClients,
): StepperGroupModel => {
  return {
    stepperId: '7',
    stepperName: $localize`Create new collection`,
    stepperGroupName: 'createNewCollectionMetadata',
    selected: false,
    visited: false,
    disable: true,
    formFields: [
      {
        formTypeId: '1',
        formFieldName: 'name',
        fieldType: FieldType.Text,
        fieldLabel: $localize`Name`,
        placeholder: $localize`:placeholder:Name`,
        inputType: 'text',
        validators: {
          required: true,
        },
        widthClass: WidthClass.WFull,
        visible: true,
        value: '',
      },
      {
        formTypeId: '2',
        formFieldName: 'description',
        fieldType: FieldType.Textarea,
        fieldLabel: $localize`Description`,
        placeholder: $localize`:placeholder:Description`,
        inputType: 'text',
        validators: {
          required: true,
        },
        widthClass: WidthClass.WFull,
        visible: true,
        value: '',
      },
      {
        formTypeId: '3',
        formFieldName: 'cover',
        fieldType: FieldType.Dropdown,
        fieldLabel: $localize`Cover`,
        dropDownSearch: true,
        inlineImage: true,
        validators: {
          required: false,
        },
        withImg: true,
        imgPreviewUrl: '',
        placeholder: $localize`:placeholder:Choose cover`,
        widthClass: WidthClass.WFull,
        options: assetListForCollection.map((asset: AssetListFileObject) => {
          return { _id: asset.key, name: asset.name, imageUrl: asset.previewUrl };
        }),
        value: '',
        visible: true,
      },
      {
        formTypeId: '4',
        formFieldName: 'owners',
        fieldType: FieldType.MultiSelectChip,
        fieldLabel: $localize`Owners`,
        validators: {
          required: false,
        },
        placeholder: $localize`:placeholder:Choose owners`,
        widthClass: WidthClass.WFull,
        options: usersLikeMe,
        value: '',
        visible: true,
        showAvatar: true,
      },
      {
        formTypeId: '5',
        formFieldName: 'keywords',
        fieldType: FieldType.MatChipListNoAutocomplete,
        fieldLabel: $localize`Tags`,
        mandatory: true,
        validators: {
          required: brandingKey === CustomClients.Standard ? false : true,
        },
        placeholder: $localize`:placeholder:Enter tags`,
        widthClass: WidthClass.WFull,
        options: [],
        visible: true,
      },
    ],
    actions: [
      {
        actionLabel: $localize`:action:Upload`,
        actionTypePosition: 'right',
        actionType: 'uploadAsset',
        icon: 'cloud_upload',
        alignIcon: 'left',
        disable: false,
        visible: true,
      },
      {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: 6,
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        visible: true,
      },
    ],
  };
};

export const getAddToExistingCollectionStepperConfig = (): StepperGroupModel => {
  return {
    stepperId: '7',
    stepperName: $localize`Add to existing collection`,
    stepperGroupName: 'addToExistingCollectionMetadata',
    selected: false,
    visited: false,
    disable: true,
    formFields: [
      {
        formTypeId: '1',
        validators: {
          required: true,
        },
        formFieldName: 'add2ExistingCollection',
        fieldType: FieldType.Dropdown,
        dropDownSearch: true,
        inlineImage: true,
        fieldLabel: $localize`Add to existing collection`,
        placeholder: $localize`:placeholder:Choose collection`,
        widthClass: WidthClass.W12,
        withImg: true,
        imgPreviewUrl: '',
        options: [],
        value: '',
        visible: true,
      },
    ],
    actions: [
      {
        actionLabel: $localize`:action:Upload`,
        actionTypePosition: 'right',
        actionType: 'uploadAsset',
        icon: 'cloud_upload',
        alignIcon: 'left',
        disable: false,
        visible: true,
      },
      {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: 6,
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        visible: true,
      },
    ],
  };
};

/**
 * Arval usage rights forms
 * @returns
 */
export const arvalUsageRightsFields = (): FormControlModel[] => [
  {
    formTypeId: '1',
    formFieldName: 'copyright',
    fieldType: FieldType.Text,
    fieldLabel: $localize`Copyright`,
    placeholder: $localize`:placeholder:Copyright`,
    inputType: 'text',
    widthClass: WidthClass.W12,
    value: '',
    visible: true,
  },
  {
    formTypeId: '2',
    formFieldName: 'country',
    fieldType: FieldType.Text,
    fieldLabel: $localize`Country`,
    placeholder: $localize`:placeholder:Country`,
    inputType: 'text',
    widthClass: WidthClass.W12,
    value: '',
    visible: true,
  },
  {
    formTypeId: '3',
    formFieldName: 'provider',
    fieldType: FieldType.Text,
    fieldLabel: $localize`Provider`,
    placeholder: $localize`:placeholder:Provider`,
    inputType: 'text',
    widthClass: WidthClass.W12,
    value: '',
    visible: true,
  },
  {
    formTypeId: '4',
    formFieldName: 'source',
    fieldType: FieldType.Text,
    fieldLabel: $localize`Source`,
    placeholder: $localize`Source`,
    inputType: 'text',
    widthClass: WidthClass.W12,
    value: '',
    visible: true,
  },
];

/**
 * Creator department based on the customer branding
 * @param brandingKey
 * @returns
 */
export const creatorDepartmentObj = (brandingKey: string) => {
  switch (brandingKey) {
    case CustomClients.Vorwerk:
      return CreatorDepartmentVorwerk;
    default:
      return CreatorDepartmentStandard;
  }
};
